"use client";
import React, { createContext, useContext, useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";

import { COMMON_COPY } from "copy/common";
import { useCopy } from "i18n/useCopy";

import { Button } from "./ui/button";
type Theme = "light" | "dark" | "system";

/** Effective light/dark appearance after resolving `system` against OS preference. */
type ResolvedTheme = "light" | "dark";

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  enableSystem?: boolean;
}

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

/**
 * Maps stored theme preference to the appearance used for CSS variables and assets.
 */
function resolveThemePreference(params: { theme: Theme; enableSystem: boolean; prefersDark: boolean }): ResolvedTheme {
  const { theme, enableSystem, prefersDark } = params;
  if (theme === "system" && enableSystem) {
    return prefersDark ? "dark" : "light";
  }
  if (theme === "dark") {
    return "dark";
  }
  return "light";
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children, defaultTheme = "system", enableSystem = true }: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first, then fall back to defaultTheme
    return (localStorage.getItem("theme") as Theme) || defaultTheme;
  });
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveThemePreference({
      theme: (localStorage.getItem("theme") as Theme) || defaultTheme,
      enableSystem: enableSystem,
      prefersDark: window.matchMedia("(prefers-color-scheme: dark)").matches,
    })
  );

  useLayoutEffect(() => {
    const root = window.document.documentElement;
    const prefersDarkMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const applyResolvedTheme = (): void => {
      const nextResolvedTheme = resolveThemePreference({
        theme: theme,
        enableSystem: enableSystem,
        prefersDark: prefersDarkMediaQuery.matches,
      });
      setResolvedTheme(nextResolvedTheme);
      root.classList.toggle("dark", nextResolvedTheme === "dark");
      root.classList.toggle("light", nextResolvedTheme === "light");
    };

    applyResolvedTheme();
    prefersDarkMediaQuery.addEventListener("change", applyResolvedTheme);
    return () => prefersDarkMediaQuery.removeEventListener("change", applyResolvedTheme);
  }, [theme, enableSystem]);

  const value = {
    theme: theme,
    resolvedTheme: resolvedTheme,
    setTheme: (nextTheme: Theme) => {
      setTheme(nextTheme);
      localStorage.setItem("theme", nextTheme);
    },
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

/** Effective light/dark appearance (resolves `system` the same way as document `dark` class). */
export function useResolvedTheme(): ResolvedTheme {
  return useTheme().resolvedTheme;
}

const ThemeProviderComponent: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = "system",
  enableSystem = true,
}) => {
  const copy = useCopy();

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b">
        <div className="container flex h-16 items-center justify-between px-4 sm:px-8">
          <h1 className="text-xl font-bold">{copy.text(COMMON_COPY.themeProviderDemo.title)}</h1>
          <Link to="/muggleTestV0/dashboard">
            <Button>{copy.text(COMMON_COPY.themeProviderDemo.backToDashboard)}</Button>
          </Link>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-2xl font-bold">{copy.text(COMMON_COPY.themeProviderDemo.title)}</h2>
          <p className="mt-4">{copy.text(COMMON_COPY.themeProviderDemo.description)}</p>
        </div>
      </main>
      <ThemeProvider defaultTheme={defaultTheme} enableSystem={enableSystem}>
        {children}
      </ThemeProvider>
    </div>
  );
};

export { ThemeProviderComponent };
